import type { RobotScheduleMapGroup, RobotScheduleMapSelectOption } from '../types/RobotScheduleMap.types';
import type { ScheduleMapSelectOption, SelectedScheduleMap } from '../types/MachineDetailsRobotSchedule.types';
import { compareNamesAscending } from './sort';
import type { Map } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';

export const MAP_GROUP_NAME_OTHERS = 'others';

const isOthersMapGroup = (groupId: string): boolean => groupId === MAP_GROUP_NAME_OTHERS;

export const compareMapGroupsAscending = (
  left: Pick<RobotScheduleMapGroup, 'groupId' | 'groupName'>,
  right: Pick<RobotScheduleMapGroup, 'groupId' | 'groupName'>,
  locale?: string
): number => {
  const leftIsOthers = isOthersMapGroup(left.groupId);
  const rightIsOthers = isOthersMapGroup(right.groupId);

  if (leftIsOthers !== rightIsOthers) {
    return leftIsOthers ? 1 : -1;
  }

  return compareNamesAscending(left.groupName, right.groupName, locale);
};

export const getSelectedMapFromSelectOption = (option: unknown): SelectedScheduleMap | undefined => {
  if (!option || typeof option !== 'object') {
    return undefined;
  }

  const selectOption = option as ScheduleMapSelectOption;

  if (!selectOption.map) {
    return undefined;
  }

  return {
    type: 'map',
    map: selectOption.map,
  };
};

export const getSelectedMapById = (id: string, maps: Map[]): SelectedScheduleMap | undefined => {
  const map = maps.find(item => item.id === id);

  if (!map) {
    return undefined;
  }

  return {
    type: 'map',
    map,
  };
};

export const mapScheduleMapsToSelectOptions = (maps: Map[], locale?: string): RobotScheduleMapSelectOption[] =>
  [...maps]
    .sort((a, b) => compareNamesAscending(a.name, b.name, locale))
    .map(map => ({
      label: map.name,
      value: map.id,
      map,
    }));

export const groupScheduleMapsByMapGroupId = (maps: Map[], locale?: string): RobotScheduleMapGroup[] => {
  const groups = new Map<string, Map[]>();
  const others: Map[] = [];

  maps.forEach(map => {
    const groupId = map.groupId;
    const groupName = map.mapGroupName;

    if (!groupId || !groupName) {
      others.push(map);
      return;
    }

    const groupByGroupId = groups.get(groupId);

    if (groupByGroupId) {
      groupByGroupId.push(map);
      return;
    }

    groups.set(groupId, [map]);
  });

  const groupedMaps = Array.from(groups.entries()).map(([groupId, groupMaps]) => ({
    groupId,
    groupName: groupMaps[0].mapGroupName ?? MAP_GROUP_NAME_OTHERS,
    maps: [...groupMaps].sort((a, b) => compareNamesAscending(a.name, b.name, locale)),
  }));

  if (others.length > 0) {
    groupedMaps.push({
      groupId: MAP_GROUP_NAME_OTHERS,
      groupName: MAP_GROUP_NAME_OTHERS,
      maps: [...others].sort((a, b) => compareNamesAscending(a.name, b.name, locale)),
    });
  }

  groupedMaps.sort((a, b) => compareMapGroupsAscending(a, b, locale));

  return groupedMaps;
};
